import {useSelector} from "react-redux";

import OpenLayersMap from "../../component/OpenLayersMap";
import OpenLayersMiniMap from "../../component/OpenLayersMiniMap";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useBasemapDataYear} from "../../hooks/useBasemapDataYear";
import {useMapSettings} from "../../hooks/useMapSettings";
import {usePdfPreview} from "../../hooks/usePdfPreview";
import {LocationPdfType} from "../../redux/pdfConstants";
import {LAYER_ALKIS, LAYER_BASEMAP, selectCapabilities} from "../../redux/wmtsSlice";
import {createLayerSwitcher, createLuftbildLayer, createScaleView, LayerFactory} from "../../util/maps";

/** Default scale denominator for the address overview map (1:250000) */
const DEFAULT_OVERVIEW_SCALE = 250000;

/**
 * Display Map with center.
 * @param {GeoRefAddress|null} address selected address meta
 * @param {string|null} map selected map
 * @param {number} [resetSignal] Zähler, der bei jedem Klick auf die Adresskachel
 *   hochgezählt wird. Bei jeder Änderung wird die Karte neu gemountet und auf den
 *   Default zurückgesetzt (Haus zentriert, 1:250k) — eine gespeicherte Ansicht
 *   (overview_map_settings) wird dann bewusst ignoriert.
 */
export default function AddressMap({address, map, resetSignal = 0}) {
    const {t, language} = useRevaTranslation();

    const capabilities = useSelector(selectCapabilities);

    const basemapCapabilities = capabilities?.[LAYER_BASEMAP];
    const basemapYear = useBasemapDataYear();

    const {initialView, onMapMoveEnd} = useMapSettings("overview_map_settings");

    const pdfPreview = usePdfPreview([LocationPdfType.OVERVIEW, LocationPdfType.LOCATION], {
        [LocationPdfType.OVERVIEW]: "Übersicht PDF erstellen",
        [LocationPdfType.LOCATION]: "Kartenset PDF erstellen",
    });

    // Nach einem Reset (Kachel-Klick) die gespeicherte Ansicht ignorieren und den
    // Default erzwingen; beim ersten Öffnen (resetSignal 0) weiterhin eine ggf.
    // gespeicherte Ansicht respektieren.
    const persistedView = resetSignal > 0 ? null : initialView;

    const createDefaultMapModel = (addressMeta) => {
        if (!addressMeta) return null;

        return {
            view: createScaleView(addressMeta, DEFAULT_OVERVIEW_SCALE, persistedView),
            layers: [
                LayerFactory.createOSMLayer("osm", {
                    title: "OpenStreetMap",
                    type: "base",
                }),
                // #19906 ohne Capabilities (Ausfall bei basemap.de) fehlt nur diese auswählbare Basisebene
                ...(basemapCapabilities
                    ? [
                          LayerFactory.createGeoserverWmtsLayer(
                              LAYER_BASEMAP,
                              undefined,
                              undefined,
                              "page_address_basemapde",
                              undefined,
                              {
                                  params: {
                                      attributions: `© GeoBasis-DE / <a target="_blank" href="https://www.bkg.bund.de">BKG</a> (${basemapYear}) <a target="_blank" href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</a>`,
                                  },
                                  title: "Basemap",
                                  type: "base",
                                  visible: false,
                                  ...basemapCapabilities,
                              },
                          ),
                      ]
                    : []),
                createLuftbildLayer(addressMeta.municipality_code, addressMeta, "address_aerial_imagery"),
                LayerFactory.createGeoserverWmsLayer("reva:brd_negativ", undefined),
                LayerFactory.createCircleLayer(address, [5000, 10000, 20000]),
                LayerFactory.createMarkerLayer(addressMeta, language),
            ],
            eventHandlers: {
                moveend_event: onMapMoveEnd,
            },
            switcher: createLayerSwitcher(t),
            scaleLine: {enabled: true},
        };
    };
    const miniMapModel = {
        view: {
            zoom: 4.5,
        },
        layers: [
            LayerFactory.createGeoserverWmsLayer(
                "reva:gemeindezentrum_in_brd_gkz",
                undefined,
                undefined,
                undefined,
                undefined,
                {params: {viewparams: `gkz:${address.municipality_code}`}},
            ),
        ],
    };

    return (
        <div className="OpenLayersMapBackdrop w-full h-full relative">
            {map !== LAYER_ALKIS && (
                <>
                    {/* #19708 ohne gültige GKZ lehnt der GeoServer-Viewparameter gkz (^\d+$) jede Kachel ab */}
                    {/^\d+$/.test(String(address.municipality_code ?? "")) && (
                        <OpenLayersMiniMap model={miniMapModel} />
                    )}
                    <OpenLayersMap
                        key={`overview-map-${resetSignal}`}
                        model={createDefaultMapModel(address)}
                        pdfPreview={pdfPreview}
                    />
                </>
            )}
        </div>
    );
}
